import { API_TYPE_GZ, WEB_COMPONENT_NETWORK_INSTALL_PATCHES } from '../../../../../webcomponents.constants';
import { customElement, query } from 'lit/decorators.js';

import { styles } from './view/styles';
import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { translateFn } from '../../../../../utils/translator.helper';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { PatchesGridWebComponent } from './internalComponents/patchesGrid';
import { getTimezone } from '../../../../../utils/global.utils';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';
import { TaskTarget } from '../../../types';
import { InstallPatchesScanTaskSpecifics } from './types';
import { GzSideMenuWebComponent } from '../../../../../ui/gzSideMenu';
import { PatchDetailsWebComponent } from './internalComponents/patchDetailsPanel';
import { PatchesGridEvents, PatchesGridItem } from './internalComponents/patchesGrid/types';
import { PatchesGridView } from './internalComponents/patchesGrid/view/PatchesGridView';

@customElement(WEB_COMPONENT_NETWORK_INSTALL_PATCHES)
export class GzInstallPatchesWebComponent extends LitElement {
  public targets: Array<TaskTarget>;

  @query('gz-install-patches-grid')
  private grid: PatchesGridWebComponent;

  @query('#automatically-reboot-checkbox')
  private shouldRebootCheckbox: GzCheckboxWebComponent;

  @query('#patchDetailsPanel')
  private patchDetailsPanel: GzSideMenuWebComponent;

  static get styles() {
    return styles;
  }

  protected firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);
    this.buildGrid();

    this.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: true },
      })
    );
  }

  private toggleSaveButtonEnabling(event: CustomEvent) {
    event.stopImmediatePropagation();

    this.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: event.detail.selectedItemsNo === 0 },
      })
    );
  }

  private handlePatchDetailsPanelOpen(event: CustomEvent) {
    const detailsPanelContent = new PatchDetailsWebComponent();
    detailsPanelContent.patchId = event.detail.rowDetails.patchId;
    detailsPanelContent.productsCount = event.detail.rowDetails.productsCount;
    this.patchDetailsPanel.setMenuContent(detailsPanelContent);
    this.patchDetailsPanel.openSideMenu();
  }

  private buildGrid() {
    if (!this.grid) {
      return;
    }

    this.grid.targets = this.targets;
    this.grid.injectDependencies({ api: { type: API_TYPE_GZ }, translateFn: translateFn, getTimezone: getTimezone });
    this.grid.start().catch(console.error);
    this.grid.addEventListener(PatchesGridEvents.SELECT_ROWS, this.toggleSaveButtonEnabling.bind(this));
    this.grid.addEventListener(PatchesGridEvents.ROW_CLICKED, this.handlePatchDetailsPanelOpen.bind(this));
  }

  public getRequestSpecifics(): InstallPatchesScanTaskSpecifics {
    if (!this.grid.controller) {
      throw Error('Controller not initiated.');
    }

    const selectedRows = (this.grid.controller.view as PatchesGridView).getSelectedGridItems();
    return {
      automaticallyReboot: this.shouldRebootCheckbox.checked,
      quickInstall: false,
      patchIds: selectedRows.map((item: PatchesGridItem) => item.patchId),
    };
  }

  render(): TemplateResult {
    return html`
      <div id="install-patches-container" class="container pt-4">
        <div class="field-container px-5 checkbox-wrapper">
          <gz-checkbox
            id="automatically-reboot-checkbox"
            label="${translateFn('Reboot endpoints after installing patches, if required')}"
          ></gz-checkbox>
        </div>
        <gz-info-box severity="${InfoBoxSeverity.WARNING}" class="px-5">
          ${translateFn(
            'Endpoints will reboot immediately after installing patches. To avoid disrupting user activity, keep this option disabled.'
          )}
        </gz-info-box>
        <div id="table-section" class="table mb-1">
          <gz-install-patches-grid></gz-install-patches-grid>
        </div>
        <gz-side-menu id="patchDetailsPanel" menuwidth="400" closeOnClickOutside resizable="left" isResponsive="true">
        </gz-side-menu>
      </div>
    `;
  }
}
